import { FCX, ID } from '@models';
import React, { ReactNode, useMemo } from 'react';
import { iTableConfigItem } from '@components/Table/TableConfig';
import { Tooltip } from 'react-tooltip';
import { getFormattedThousand } from '@core/utils/number';
import { TableContext } from '@components/Table/TableContext';
import { iSeasonalFood } from '@api/useSeasonalByFoods';

const FoodFlavorsTableConfig: FCX<{
    id: ID;
    data: iSeasonalFood[];
    renderSeasonName: ReactNode;
    sortingColumn: keyof iSeasonalFood;
}> = ({
    id,
    data,
    renderSeasonName,
    sortingColumn,
    children,
}) => {
    const tableConfig = useMemo((): iTableConfigItem[] => [
        {
            key: 'name',
            title: '',
            Value: ({ index }) => <>
                {data[index].name}
            </>,
        },
        {
            key: 'index',
            sortingColName: 'index',
            title: <>
                Index
                <Tooltip id={`table-seasonal-foods-${id}-index`}>
                    <div style={{ textAlign: 'center', width: 180 }}>
                        100 = average for all seasons;<br/>
                        A value of {'>'}100 indicates<br/>
                        this flavor / ingredient is more prevalent in&nbsp;
                        {renderSeasonName}
                        &nbsp;launches
                    </div>
                </Tooltip>
            </>,
            Value: ({ index }) => {
                const value = data[index].index;
                return (
                    <>
                        {value === Infinity ? '*' : getFormattedThousand(value)}
                    </>
                );
            },
            getAttrs: (index) => (index === -1 ? {
                'data-tooltip-id': `table-seasonal-foods-${id}-index`,
            } : {}),
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                    backgroundColor: sortingColumn === 'index' ? '#fffae5' : undefined,
                },
            },
        },
        {
            key: 'count',
            sortingColName: 'count',
            title: 'Count',
            Value: ({ index }) => <>
                {getFormattedThousand(data[index].count)}
            </>,
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                    backgroundColor: sortingColumn === 'count' ? '#fffae5' : undefined,
                },
            },
        },
        {
            key: 'percentage',
            sortingColName: 'percentage',
            title: <>
                Percentage
                <Tooltip id={`table-seasonal-foods-${id}-percentage`}>
                    <div style={{ textAlign: 'center', width: 100 }}>
                        % of total{' '}
                        {renderSeasonName}
                        {' '}menu launches
                    </div>
                </Tooltip>
            </>,
            Value: ({ index }) => <>
                {Math.round(data[index].percentage || 0)}%
            </>,
            getAttrs: (index) => (index === -1 ? {
                'data-tooltip-id': `table-seasonal-foods-${id}-percentage`,
            } : {}),
            styles: {
                cell: {
                    flex: '0 0 20%',
                    justifyContent: 'center',
                    backgroundColor: sortingColumn === 'percentage' ? '#fffae5' : undefined,
                },
                head: {
                    cursor: 'pointer',
                }
            },
        },
    ], [data, renderSeasonName, sortingColumn, id]);

    return (
        <TableContext.Provider value={{ config: tableConfig }}>
            {children}
        </TableContext.Provider>
    );
};

export default FoodFlavorsTableConfig;